// UPPDRAG-54 / UPPDRAG-55 — pure helpers for the speltider grid:
//   - minute-overlap conflict detection between division speltider
//     (catches real overlap, not just identical time-slots like the
//     legacy detectConflicts in LeagueDivisionTimes did)
//   - addMinutesToTime for the auto-end-time helper used when admin
//     picks a start time
//
// Pure (no I/O); covered by vitest in src/test/lib/speltiderConflicts.test.ts.

/** "HH:MM" → minutes since midnight. */
export function timeToMinutes(t: string): number {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Minutes since midnight → "HH:MM" (24h, zero-padded). Wraps at 24h. */
export function minutesToTime(mins: number): string {
  const wrapped = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * UPPDRAG-55: derive an end time from a start by adding match-duration
 * minutes. Caps at 23:59 if the addition would wrap past midnight (the
 * speltider grid in LeagueDivisionTimes only generates time options up
 * to 23:30 anyway).
 */
export function addMinutesToTime(start: string, addMinutes: number): string {
  if (!start) return start;
  const total = timeToMinutes(start) + addMinutes;
  if (total >= 24 * 60) return "23:59";
  return minutesToTime(total);
}

/** Half-open minute-range overlap: [a.start, a.end) intersects [b.start, b.end). */
export function minutesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end;
}

/** Range form using "HH:MM" strings — convenience wrapper. */
export function timeRangesOverlap(
  a: { start: string; end: string },
  b: { start: string; end: string },
): boolean {
  if (!a.start || !a.end || !b.start || !b.end) return false;
  return minutesOverlap(
    { start: timeToMinutes(a.start), end: timeToMinutes(a.end) },
    { start: timeToMinutes(b.start), end: timeToMinutes(b.end) },
  );
}

// ── Speltider conflict detection ───────────────────────────────────────

export interface SpeltidBlock {
  start: string;
  end: string;
  court_ids: string[];
}

export interface SpeltidDivision {
  id: string;
  name: string;
  /** day-key → list of blocks. Same shape as PlayScheduleMap in LeagueDivisionTimes. */
  schedule: Record<string, SpeltidBlock[]>;
}

export interface CourtConflict {
  /** Division that owns the conflicting block (NOT the one being edited). */
  conflictingDivisionId: string;
  conflictingDivisionName: string;
  day: string;
  start: string;
  end: string;
  courtId: string;
}

/**
 * UPPDRAG-54: For a candidate (day, courtId, start, end) being edited in
 * (excludeDivisionId, excludeBlockIndex), find every other division block
 * that overlaps in minutes on the same day + court.
 *
 * Used by the speltider court-badge render to disable + highlight courts
 * that would conflict if the admin clicked them. Real-time, sub-millisecond
 * for typical league sizes (5-10 divisions × 7 days × few blocks each).
 */
export function findCourtConflicts(
  divisions: SpeltidDivision[],
  candidate: {
    day: string;
    courtId: string;
    start: string;
    end: string;
    excludeDivisionId: string;
    excludeBlockIndex: number;
  },
): CourtConflict[] {
  if (!candidate.start || !candidate.end) return [];
  const out: CourtConflict[] = [];
  const candRange = { start: timeToMinutes(candidate.start), end: timeToMinutes(candidate.end) };
  if (candRange.end <= candRange.start) return [];

  for (const div of divisions) {
    const blocks = div.schedule[candidate.day] ?? [];
    for (let i = 0; i < blocks.length; i++) {
      // Skip the block being edited (don't flag self-conflict).
      if (div.id === candidate.excludeDivisionId && i === candidate.excludeBlockIndex) continue;
      const block = blocks[i];
      if (!block.court_ids?.includes(candidate.courtId)) continue;
      const blockRange = { start: timeToMinutes(block.start), end: timeToMinutes(block.end) };
      if (blockRange.end <= blockRange.start) continue;
      if (!minutesOverlap(candRange, blockRange)) continue;
      out.push({
        conflictingDivisionId: div.id,
        conflictingDivisionName: div.name,
        day: candidate.day,
        start: block.start,
        end: block.end,
        courtId: candidate.courtId,
      });
    }
  }
  return out;
}

/**
 * Whole-grid overlap detection — replaces the legacy exact-key detector
 * in LeagueDivisionTimes (which only caught identical slots, missing
 * 17:30-19:00 vs 18:00-19:30 overlaps).
 *
 * Returns one entry per pair of overlapping blocks (deduplicated so we
 * report each overlap only once even though the helper inspects both
 * sides of the pair).
 */
export interface ScheduleOverlap {
  day: string;
  courtId: string;
  /** Sorted alphabetically so dedup-key is stable. */
  divisionNames: string[];
  rangeA: { start: string; end: string };
  rangeB: { start: string; end: string };
}

export function findAllSpeltidOverlaps(divisions: SpeltidDivision[]): ScheduleOverlap[] {
  const seen = new Set<string>();
  const out: ScheduleOverlap[] = [];
  for (let i = 0; i < divisions.length; i++) {
    for (let j = i + 1; j < divisions.length; j++) {
      const a = divisions[i];
      const b = divisions[j];
      // Iterate every (day, block) pair across the two divisions.
      for (const day of Object.keys({ ...a.schedule, ...b.schedule })) {
        const aBlocks = a.schedule[day] ?? [];
        const bBlocks = b.schedule[day] ?? [];
        for (const ab of aBlocks) {
          for (const bb of bBlocks) {
            const sharedCourts = (ab.court_ids ?? []).filter((c) => bb.court_ids?.includes(c));
            if (sharedCourts.length === 0) continue;
            if (!timeRangesOverlap(ab, bb)) continue;
            for (const courtId of sharedCourts) {
              const names = [a.name, b.name].sort();
              const key = `${day}|${courtId}|${names.join("/")}|${ab.start}|${ab.end}|${bb.start}|${bb.end}`;
              if (seen.has(key)) continue;
              seen.add(key);
              out.push({
                day,
                courtId,
                divisionNames: names,
                rangeA: { start: ab.start, end: ab.end },
                rangeB: { start: bb.start, end: bb.end },
              });
            }
          }
        }
      }
    }
  }
  return out;
}
